import { api } from '../libs/api';
import { getRequestConfig } from '../utils/auth';


interface CreateSubscriptionRequest {
  priceId?: string; // Made optional since we can use customPrice instead
  agencyId?: string; // Optional agency ID for associating with the subscription
  customerData: {
    name: string;
    email: string;
    phone?: string;
    address?: {
      line1: string;
      city: string;
      postal_code: string;
      country: string;
    };
  };
  discountPercentage?: number;
  couponCode?: string;
  customPrice?: number; // New parameter for dynamic pricing
}

interface CreateSubscriptionResponse {
  success: boolean;
  data?: {
    subscriptionId: string;
    clientSecret: string | null;
    customerId: string;
    status: string;
    requiresPayment?: boolean;
    priceId?: string; // Include the price ID that was used
  };
  error?: string;
}



interface ConfirmPaymentRequest {
  paymentIntentId: string;
  agencyId?: string;
}

interface ConfirmPaymentResponse {
  success: boolean;
  data?: {
    status: string;
    amount: number;
    currency: string;
    metadata: Record<string, string>;
  };
  error?: string;
}

interface PaymentStatusResponse {
  success: boolean;
  data?: {
    id: string;
    status: string;
    amount: number;
    currency: string;
    created: Date;
    metadata: Record<string, string>;
  };
  error?: string;
}

interface CouponValidationResponse {
  success: boolean;
  data?: {
    valid: boolean;
    couponCode?: string;
    promotionCode?: string;
    percentage?: number;
    amountOff?: number;
    currency?: string;
    name?: string;
    duration?: string;
    message?: string;
  };
  error?: string;
}

export interface Invoice {
  id: string;
  number?: string;
  date: string;
  amount: string;
  status: string;
  downloadUrl?: string;
  pdfUrl?: string;
  description?: string;
  subtotal: number;
  tax: number;
  total: number;
  currency: string;
  periodStart?: string | null;
  periodEnd?: string | null;
  amount_paid?: number;
  amount_due?: number;
  payment_intent?: {
    id: string;
    status: string;
    client_secret?: string;
    next_action?: any;
  } | null;
  requires_action?: boolean;
  payment_error?: string;
}

interface InvoicesResponse {
  success: boolean;
  data?: Invoice[];
  error?: string;
}

export interface Subscription {
  id: string;
  status: string;
  current_period_start: string;
  current_period_end: string;
  customer: {
    id: string;
    email: string | null;
    name: string | null;
  };
  items: Array<{
    id: string;
    price: {
      id: string;
      currency: string;
      unit_amount: number;
      recurring: {
        interval: string;
        interval_count: number;
      } | null;
    };
    quantity: number;
  }>;
  latest_invoice: {
    id: string;
    status: string | null;
    total: number | null;
    currency: string | null;
  } | null;
  discount: {
    coupon: {
      id: string;
      name: string | null;
      percent_off: number | null;
      amount_off: number | null;
      currency: string | null;
      duration: string;
    };
  } | null;
  cancel_at_period_end: boolean;
  canceled_at: string | null;
  created: string;
  metadata: Record<string, string>;
}

interface SubscriptionResponse {
  success: boolean;
  data?: {
    subscription: Subscription;
  };
  error?: string;
}

interface UpdateSeatsRequest {
  seats: number;
}

interface UpdateSeatsResponse {
  success: boolean;
  data?: {
    subscription: Subscription;
    message: string;
  };
  error?: string;
}

interface UpdateSubscriptionRequest {
  seats?: number;
  couponCode?: string;
}

interface UpdateSubscriptionResponse {
  success: boolean;
  data?: {
    subscription: Subscription;
    message: string;
    invoice?: {
      id: string;
      amount_paid: number;
      amount_due: number;
      status: string;
      total: number;
      currency: string;
      payment_intent?: {
        id: string;
        status: string;
      } | null;
    };
  };
  error?: string;
}

class PaymentService {



  async confirmPayment(data: ConfirmPaymentRequest): Promise<ConfirmPaymentResponse> {
    try {
      const response = await api.post('/payments/confirm', data, getRequestConfig());
      return response;
    } catch (error: any) {
      console.error('Error confirming payment:', error);
      return {
        success: false,
        error: error.error || 'Failed to confirm payment'
      };
    }
  }

  async getPaymentStatus(subscriptionId: string): Promise<PaymentStatusResponse> {
    try {
      const response = await api.get(`/payments/status/${subscriptionId}`, getRequestConfig());
      return response;
    } catch (error: any) {
      console.error('Error getting payment status:', error);
      return {
        success: false,
        error: error.error || 'Failed to get payment status'
      };
    }
  }

  async createSubscription(data: CreateSubscriptionRequest): Promise<CreateSubscriptionResponse> {
    try {
      const response = await api.post('/payments/create-subscription', data, getRequestConfig());
      return response;
    } catch (error: any) {
      console.error('Error creating subscription:', error);
      return {
        success: false,
        error: error.error || 'Failed to create subscription'
      };
    }
  }

  async validateCoupon(couponCode: string): Promise<CouponValidationResponse> {
    try {
      const response = await api.get(`/payments/validate-coupon/${encodeURIComponent(couponCode)}`, getRequestConfig());
      return response;
    } catch (error: any) {
      console.error('Error validating coupon:', error);
      return {
        success: false,
        error: error.error || 'Failed to validate coupon'
      };
    }
  }

  async getInvoicesByAgencyId(agencyId: string): Promise<InvoicesResponse> {
    try {
      const response = await api.get(`/payments/invoices/agency/${agencyId}`, getRequestConfig());
      return response;
    } catch (error: any) {
      console.error('Error fetching invoices by agency ID:', error);
      return {
        success: false,
        error: error.error || 'Failed to fetch invoices'
      };
    }
  }

  async getInvoicesByUserId(userId: string): Promise<InvoicesResponse> {
    try {
      const response = await api.get(`/payments/invoices/user/${userId}`, getRequestConfig());
      return response;
    } catch (error: any) {
      console.error('Error fetching invoices by user ID:', error);
      return {
        success: false,
        error: error.error || 'Failed to fetch invoices'
      };
    }
  }

  async getSubscription(subscriptionId: string): Promise<SubscriptionResponse> {
    try {
      const response = await api.post(`/payments/subscription/${subscriptionId}`, {}, getRequestConfig());
      return response;
    } catch (error: any) {
      console.error('Error fetching subscription:', error);
      return {
        success: false,
        error: error.error || 'Failed to fetch subscription'
      };
    }
  }

  async updateSubscription(subscriptionId: string, updates: UpdateSubscriptionRequest): Promise<UpdateSubscriptionResponse> {
    try {
      const response = await api.put(`/payments/subscription/${subscriptionId}`, updates, getRequestConfig());
      return response;
    } catch (error: any) {
      console.error('Error updating subscription:', error);
      return {
        success: false,
        error: error.error || 'Failed to update subscription'
      };
    }
  }

  // Backward compatibility method
  async updateSubscriptionSeats(subscriptionId: string, seats: number): Promise<UpdateSeatsResponse> {
    return this.updateSubscription(subscriptionId, { seats });
  }


}

export default new PaymentService(); 